import { Ionicons } from '@expo/vector-icons';
import { router, type Href } from 'expo-router';
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { useBackTo } from '@/hooks/use-back-to';

interface Props {
	associationId: string;
	associationName?: string;
	title: string;
	subtitle?: string;
	right?: React.ReactNode;
	/**
	 * Override the back destination. Takes priority over the URL `?backTo=`
	 * param. Pass when a page should always return to a specific location
	 * regardless of how the user arrived.
	 */
	backTo?: string;
	/** Custom back-link label. Defaults to "Back to <association>" / "Back". */
	backLabel?: string;
	children: React.ReactNode;
}

/**
 * Shared chrome for association-scoped sub-pages.
 *
 * BUILDING A NEW REPORT PAGE? Every date this page derives for itself — the
 * `asOf` default, a period start/end, the fallback when a cleared `DateInput`
 * hands back `null` — must come from the Eastern-anchored helpers in
 * `utils/date-utils.ts` / `utils/report-dates.ts` (`todayBusinessMs`,
 * `todayBusinessDate`, `previousMonthEnd`, `yearToDate`, …). A bare
 * `new Date()` / `Date.now()` is wrong twice: it reports as of *tomorrow*
 * after 8 p.m. ET, and in a non-ET zone it lands a whole month (or year) off
 * around the 1st. Sending it as an explicit `?asOf=` also bypasses the
 * server's ET default. See CLAUDE.md → "Report dates are ALWAYS
 * Eastern-anchored".
 *
 * Back-link resolution (most specific first):
 *   1. `backTo` prop (explicit caller override)
 *   2. `?backTo=` URL param via `useBackTo` (drill-down trail)
 *   3. The hub's Accounting tab
 *
 * The association ID is authoritative from the route, so none of these
 * pages show an association picker.
 */
export const AssociationSubPage: React.FC<Props> = ({
	associationId,
	associationName,
	title,
	subtitle,
	right,
	backTo,
	backLabel,
	children,
}) => {
	const back = useBackTo();
	const resolvedTarget = backTo ?? back.backTo;
	const defaultLabel = associationName ? `Back to ${associationName}` : 'Back to dashboard';
	const label = backLabel ?? (resolvedTarget ? 'Back' : defaultLabel);

	const onBack = (): void => {
		if (resolvedTarget) {
			router.replace(resolvedTarget as Href);
			return;
		}
		// replace, not push: the page you leave must not stay in history as a forward step.
		router.replace(`/associations/${associationId}?tab=accounting` as Href);
	};

	return (
		<View style={styles.page}>
			<View style={styles.header}>
				<Pressable onPress={onBack} style={styles.back}>
					<Ionicons name="chevron-back" size={16} color="#1976d2" />
					<Text style={styles.backLabel}>{label}</Text>
				</Pressable>
				<View style={styles.titleRow}>
					<View style={{ flex: 1 }}>
						<Text style={styles.title}>{title}</Text>
						{subtitle && <Text style={styles.sub}>{subtitle}</Text>}
					</View>
					{right && <View style={styles.right}>{right}</View>}
				</View>
			</View>
			<ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>{children}</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	page: { flex: 1, backgroundColor: '#f5f7fa' },
	header: { padding: 16, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#e0e7ef' },
	back: { flexDirection: 'row', alignItems: 'center', gap: 2, marginBottom: 8 },
	backLabel: { color: '#1976d2', fontSize: 14 },
	titleRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
	title: { fontSize: 22, fontWeight: '700', color: '#212121' },
	sub: { color: '#546e7a', marginTop: 2 },
	right: { flexDirection: 'row', alignItems: 'center', gap: 8 },
	content: { flex: 1 },
	contentInner: { padding: 16 },
});
